import React from "react"
import { Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"

import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { TABLE_OPERATION_OPTIONS } from "features/Reporting/hooks/useOperatorManagement"

import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { SelectOption } from "types/ui.types"

/**
 * Props for TimeSeriesVariableSelector component
 */
export interface TimeSeriesVariableSelectorProps {
  /** Available variable options */
  selectOptions: SelectOption[]
  /** Currently selected primary variable */
  selectedPrimaryVariable: string | null
  /** Currently selected breakdown variable */
  selectedBreakdownVariable: string | null
  /** Currently selected wave variable */
  selectedWaveVariable: string | null
  /** Currently selected statistic type */
  selectedStatisticType: STATISTIC_TYPE
  /** Currently selected table operation type */
  tableOperationType: TABLE_OPERATION_TYPE
  /** Available statistic types */
  availableStatisticTypes: STATISTIC_TYPE[]
  /** Whether component is in loading state */
  isLoading: boolean
  /** Variable selection handler */
  onVariableSelect: (
    value: string,
    type: "primary" | "breakdown" | "wave"
  ) => void
  /** Statistic type change handler */
  onStatisticTypeChange: (value: STATISTIC_TYPE) => void
  /** Table operation type change handler */
  onTableOperationTypeChange: (value: TABLE_OPERATION_TYPE) => void
  /** Refresh/generate handler */
  onGenerate: () => void
}

/**
 * TimeSeriesVariableSelector Component
 *
 * Handles variable selection interface for time series charts.
 * Provides dropdowns for primary, breakdown, and wave variables,
 * along with statistic type and table operation configuration.
 */
const TimeSeriesVariableSelector: React.FC<TimeSeriesVariableSelectorProps> = ({
  selectOptions,
  selectedPrimaryVariable,
  selectedBreakdownVariable,
  selectedWaveVariable,
  selectedStatisticType,
  tableOperationType,
  availableStatisticTypes,
  isLoading,
  onVariableSelect,
  onStatisticTypeChange,
  onTableOperationTypeChange,
  onGenerate,
}) => {
  const hasAllRequiredVariables = !!(
    selectedPrimaryVariable &&
    selectedBreakdownVariable &&
    selectedWaveVariable
  )

  return (
    <div className="p-2 bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
      <div className="flex flex-col space-y-1.5">
        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Table Operation Type
          </label>
          <Select
            placeholder="Select operation type"
            className="w-full text-xs"
            value={tableOperationType}
            onChange={onTableOperationTypeChange}
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px", minWidth: "250px" }}
            optionLabelProp="label"
          >
            {TABLE_OPERATION_OPTIONS.map((opt) => (
              <Select.Option
                key={opt.value}
                value={opt.value}
                label={opt.label}
              >
                <div>
                  <div style={{ fontWeight: "500" }}>{opt.label}</div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#666",
                      marginTop: "2px",
                    }}
                  >
                    {opt.description}
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Primary Variable
          </label>
          <Select
            placeholder="Select a primary variable"
            showSearch
            className="w-full text-xs"
            value={selectedPrimaryVariable}
            onChange={(value) => onVariableSelect(value, "primary")}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children
                ?.toString()
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          >
            {selectOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Breakdown Variable
          </label>
          <Select
            placeholder="Select a breakdown variable"
            showSearch
            className="w-full text-xs"
            value={selectedBreakdownVariable}
            onChange={(value) => onVariableSelect(value, "breakdown")}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children
                ?.toString()
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          >
            {selectOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Wave Variable
          </label>
          <Select
            placeholder="Select a wave variable"
            showSearch
            className="w-full text-xs"
            value={selectedWaveVariable}
            onChange={(value) => onVariableSelect(value, "wave")}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option?.children
                ?.toString()
                .toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          >
            {selectOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium mb-0.5 block">
            Statistic Type
          </label>
          <Select
            placeholder="Select statistic type"
            className="w-full text-xs"
            value={selectedStatisticType}
            onChange={onStatisticTypeChange}
            size="small"
            style={{ fontSize: "11px" }}
            dropdownStyle={{ fontSize: "11px" }}
          >
            {availableStatisticTypes.map((stat) => (
              <Select.Option key={stat} value={stat}>
                {stat}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="flex gap-1 pt-1">
          <div className="flex items-center text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded-md">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="mr-1 text-blue-400"
              style={{ fontSize: "10px" }}
            />
            Select all three variables and generate
          </div>
        </div>

        <div className="pt-1">
          <DataQButton
            type="primary"
            onClick={onGenerate}
            isDisabled={!hasAllRequiredVariables || isLoading}
            size="small"
            isLoading={isLoading}
            className="w-full"
          >
            {isLoading ? "Generating..." : "Generate Time Series Chart"}
          </DataQButton>
        </div>
      </div>
    </div>
  )
}

export default TimeSeriesVariableSelector
